/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo, useEffect } from 'react';
import { useMutation } from '@apollo/react-hooks';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import Modal from '@salesforce/design-system-react/components/modal';
import Spinner from '@salesforce/design-system-react/components/spinner';
import Button from '@salesforce/design-system-react/components/button';
import { useSelector } from 'react-redux';

import CartComponent from '../CartComponent';
import useSnackBar from '../../../../../../util/customHooks/useSnackBar';
import SnackBar from '../../../../../Shared/SnackBar';
import { getUserInfo } from '../../../../../../store/ducks/userInfo';
import { NOTIFY_TYPE, USAGE_TYPE, STALE_RECORD_MESSAGE, SYSTEM_NAME, DEFAULT_VERSION } from '../../../../../../util/constants';
import { isStaleRecordError } from '../../../../../../util/optimisticLock';
import RESUBMIT_ORDER_DETAILS from '../../../../../../graphql/mutations/resubmitOrderDetails';
import CREATE_UPDATE_USAGE_LINE_ITEMS from '../../../../../../graphql/mutations/createUpdateUsageSheetLineItems';

import useParts, { INVENTORY_CHANNELS } from './useParts';
import InventorySection from './InventorySection/InventorySection';
import SearchBar from './SearchBar/SearchBar';
import { buildPayload } from './helper';
import styles from './styles.module.scss';

interface AddPartsModalProps {
  isOpen: boolean;
  handleClose: () => void;
  usageDetails: any;
  refetch: () => void;
  isERPOrder: boolean;
  count: number;
  modlenData?: any;
}

const AddPartsModal = ({
  isOpen,
  handleClose,
  usageDetails,
  refetch,
  isERPOrder,
  count,
  modlenData,
}: AddPartsModalProps) => {
  const userInfo = useSelector(getUserInfo);
  const { open, notification, openSnackBar } = useSnackBar();
  const isOps = userInfo && userInfo?.personas?.toLowerCase().indexOf('op') > -1;
  const isCSR = userInfo && userInfo?.personas?.toLowerCase().indexOf('csr') > -1;
  const isBillOnly = usageDetails?.diplayOrderType === USAGE_TYPE.BILL_ONLY;
  const isShipAndBill = usageDetails?.diplayOrderType === USAGE_TYPE.SHIP_AND_BILL;
  const showMaterialVariant = isBillOnly || isShipAndBill;
  const [isSaving, setIsSaving] = useState(false);

  const [resubmitOrderLineItems] = useMutation(RESUBMIT_ORDER_DETAILS);
  const [
    createUpdateUsageLineItems,
    { data: usageLineItems, loading: loadingLineItems },
  ] = useMutation(CREATE_UPDATE_USAGE_LINE_ITEMS);

  const catalogVisibility = useMemo(() => {
    const rule = userInfo?.businessRules.find(
      (br: any) => br.rules === 'Allow Add from Catalog on Bill Only'
    );
    return rule?.isRequired ?? false;
  }, [userInfo]);

  const {
    searchText,
    searchInventory,
    inventoryCount,
    repstockInventory,
    loadingRepstockInventory,
    customerInventory,
    loadingCustomerInventory,
    catalogInventory,
    loadingCatalogInventory,
    handleInventoryCount,
    handleCatalogCount,
    inventoryPartsToAdd,
    catalogPartsToAdd,
    productGroups,
    loadingProductGroups,
    selectedGroup,
    setSelectedGroup,
  } = useParts({
    usageDetails,
    userInfo,
    shouldLoadCatalogByDefault: isShipAndBill,
    shouldSkipCustomer: !isBillOnly,
    shouldSkipRepstock: !isBillOnly,
    initialPartsCount: count,
    openSnackBar,
  });

  const catalogRules = catalogVisibility || isOps || isCSR || isShipAndBill;
  const shouldShowCatalog = isShipAndBill ? catalogRules : catalogRules && searchText.length > 2;

  const onAddClick = async (): Promise<void> => {
    setIsSaving(true);

    const payload = buildPayload(
      usageDetails,
      catalogPartsToAdd,
      inventoryPartsToAdd,
      modlenData,
      isERPOrder
    );

    if (isERPOrder) {
      const mutation = {
        id: usageDetails?.id,
        salesRepId: usageDetails?.salesRepId,
        accountId: usageDetails?.accountId,
        submitType: 'Update',
        requestedFrom:
          usageDetails?.diplayOrderType === USAGE_TYPE.BILL_ONLY ? 'BillOnly' : 'ShipAndBill',
        lineItems: payload,
        expectedCaseVersion: usageDetails?.caseVersion,
      };
      resubmitOrderLineItems({
        variables: mutation,
      })
        .then((res: any): void => {
          if (res && res.data?.resubmitOrderDetails?.message === 'success') {
            handleClose();
            refetch();
          }
        })
        .catch((err: any) => {
          if (isStaleRecordError(err)) {
            openSnackBar(NOTIFY_TYPE.ERROR, STALE_RECORD_MESSAGE);
          }
        })
        .finally(() => {
          setIsSaving(false);
        });
    } else {
      // split out new parts and previously added parts
      const [prevParts, newParts] = payload.reduce(
        ([p, n], obj) => {
          if (obj.externalId) {
            p.push(obj);
          } else {
            n.push(obj);
          }
          return [p, n];
        },
        [[], []]
      );

      let addingCount = 0;
      /* Only dismiss the modal once every save succeeded - closing on a
         rejection makes a refused add look like it went through. */
      let addFailed = false;
      if (newParts && newParts.length > 0) {
        addingCount += 1;
        const mutation = {
          caseId: usageDetails.caseId,
          caseExternalId: usageDetails.caseExternalId,
          salesRepId: usageDetails.salesRepId,
          formType: 'New',
          lineItems: newParts,
          system: SYSTEM_NAME,
          version: process.env.REACT_APP_VERSION || DEFAULT_VERSION,
        };
        createUpdateUsageLineItems({
          variables: {
            ...mutation,
            expectedCaseVersion: usageDetails?.caseVersion,
          },
        })
          .catch(error => {
            /* Resync so a rejected change does not linger on screen. */
            addFailed = true;
            if (isStaleRecordError(error)) {
              openSnackBar(NOTIFY_TYPE.ERROR, STALE_RECORD_MESSAGE);
              return;
            }
            const errMsg = error?.message?.includes('Error:')
              ? error.message.split('Error:')[1]
              : error.message.split(':')[1] || 'Something went wrong. Please try again.';
            openSnackBar(NOTIFY_TYPE.ERROR, errMsg);
          })
          .finally(() => {
            addingCount -= 1;
            if (!addingCount) {
              setIsSaving(false);
              if (!addFailed) {
                handleClose();
              }
            }
          });
      }

      if (prevParts && prevParts.length > 0) {
        addingCount += 1;
        const mutationUpdate = {
          caseId: usageDetails.caseId,
          caseExternalId: usageDetails.caseExternalId,
          salesRepId: usageDetails.salesRepId,
          formType: 'Update',
          lineItems: prevParts,
          system: SYSTEM_NAME,
          version: process.env.REACT_APP_VERSION || DEFAULT_VERSION,
        };
        createUpdateUsageLineItems({
          variables: {
            ...mutationUpdate,
            expectedCaseVersion: usageDetails?.caseVersion,
          },
        })
          .catch(error => {
            /* Resync so a rejected change does not linger on screen. */
            addFailed = true;
            if (isStaleRecordError(error)) {
              openSnackBar(NOTIFY_TYPE.ERROR, STALE_RECORD_MESSAGE);
              return;
            }
            const errMsg = error?.message?.includes('Error:')
              ? error.message.split('Error:')[1]
              : error.message.split(':')[1] || 'Something went wrong. Please try again.';
            openSnackBar(NOTIFY_TYPE.ERROR, errMsg);
          })
          .finally(() => {
            addingCount -= 1;
            if (!addingCount) {
              setIsSaving(false);
              if (!addFailed) {
                handleClose();
              }
            }
          });
      }

      if (!addingCount) {
        setIsSaving(false);
      }
    }
  };

  useEffect(() => {
    if (usageLineItems && usageLineItems.createUpdateUsageSheetLineItems) {
      if (usageLineItems.createUpdateUsageSheetLineItems.message === 'success') {
        handleClose();
        refetch();
      }
    }
  }, [handleClose, refetch, usageLineItems]);

  return (
    <IconSettings iconPath="/icons">
      <>
        <SnackBar open={open} notification={notification} />
        <Modal
          ariaHideApp={false}
          isOpen={isOpen}
          footer={[
            <div className="usage-modal-footer">
              {(isSaving ||
                loadingCustomerInventory ||
                loadingRepstockInventory ||
                loadingCatalogInventory) && <Spinner size="small" />}
              <Button
                label="Cancel"
                onClick={() => {
                  handleClose();
                }}
              />
              <Button label={<span data-testid="parts-modal-add-btn">Add</span>} variant="brand" onClick={onAddClick} />
            </div>,
          ]}
          onRequestClose={() => {
            handleClose();
          }}
          dismissOnClickOutside={false}
        >
          <div className={styles.cartComponentContainer}>
            <p className={styles.title}>Add Parts</p>
            <div className={styles.cartComponentAlign}>
              <CartComponent count={inventoryCount} color="black" textColor="white" />
            </div>
          </div>
          <div className={styles.modalInnerContainer}>
            {loadingLineItems && <Spinner size="large" />}
            <SearchBar
              searchInventory={searchInventory}
              loading={loadingRepstockInventory || loadingCustomerInventory}
              productGroups={productGroups}
              loadingProductGroups={loadingProductGroups}
              selectedGroup={selectedGroup}
              setSelectedGroup={setSelectedGroup}
            />
            <div className={styles.sectionsContainer}>
              {isBillOnly && (
                <>
                  <InventorySection
                    title="Customer"
                    searching={searchText.length > 2}
                    inventoryType={INVENTORY_CHANNELS.CUSTOMER}
                    inventory={customerInventory}
                    loading={loadingCustomerInventory}
                    handleInventoryCounter={handleInventoryCount}
                    showMaterialVariant={showMaterialVariant}
                  />
                  <InventorySection
                    title="Repstock"
                    searching={searchText.length > 2}
                    inventoryType={INVENTORY_CHANNELS.REPSTOCK}
                    inventory={repstockInventory}
                    loading={loadingRepstockInventory}
                    handleInventoryCounter={handleInventoryCount}
                    showMaterialVariant={showMaterialVariant}
                  />
                </>
              )}
              {shouldShowCatalog && (
                <InventorySection
                  title="Catalog"
                  searching={searchText.length > 2}
                  inventoryType={INVENTORY_CHANNELS.CATALOG}
                  inventory={catalogInventory}
                  loading={loadingCatalogInventory}
                  handleCatalogCounter={handleCatalogCount}
                  showMaterialVariant={showMaterialVariant}
                />
              )}
            </div>
          </div>
        </Modal>
      </>
    </IconSettings>
  );
};

export default AddPartsModal;
